import React from 'react'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'

interface StatCardProps {
  title: string
  value: string | number
  trend?: number
  trendLabel: string
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  sparklinePoints: number[]
  sparklineStroke: string
}

export const StatCard = ({
  title,
  value,
  trend,
  trendLabel,
  icon,
  iconBg,
  iconColor,
  sparklinePoints,
  sparklineStroke,
}: StatCardProps) => {
  const hasTrend = trend !== undefined
  const isPositive = (trend ?? 0) >= 0
  const formattedTrend = `${isPositive ? '+' : ''}${trend}%`

  // Ensure sparklinePoints always has multiple points so Recharts draws a line graph
  let rawPoints = Array.isArray(sparklinePoints) && sparklinePoints.length > 0 ? sparklinePoints : [0]
  if (rawPoints.length === 1) {
    const val = rawPoints[0]
    rawPoints = [val * 0.4, val * 0.75, val * 0.9, val]
  }

  // Map raw array data to Recharts compatible object array
  const chartData = rawPoints.map((val, idx) => ({
    id: idx,
    value: val,
  }))

  const cleanChartId = `sparkGrad-${title.replace(/[^a-zA-Z0-9]/g, '-')}`

  return (
    <div className="rounded-card border border-border bg-surface p-5 shadow-soft hover:shadow-card hover:-translate-y-0.5 transition-all duration-300 text-left flex flex-col justify-between h-full min-h-[150px]">
      {/* Row 1: Icon & Title */}
      <div className="flex items-center gap-3">
        {/* Icon Badge - flex-shrink-0 ensures it stays a perfect circle */}
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full ${iconBg} ${iconColor} border border-current/10 flex-shrink-0`}
        >
          {icon}
        </div>
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-text-secondary whitespace-nowrap">
          {title}
        </span>
      </div>

      {/* Row 2: Value & Sparkline */}
      <div className="mt-3 flex items-center justify-between gap-4">
        <span className="text-2xl font-black text-text-primary tracking-tight whitespace-nowrap">{value}</span>

        {/* Recharts Sparkline Area Chart - positioned in between Title and Trend */}
        <div className="w-24 h-8 flex-shrink-0 overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <defs>
                <linearGradient id={cleanChartId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={sparklineStroke} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={sparklineStroke} stopOpacity="0.0" />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={sparklineStroke}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#${cleanChartId})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 3: Trend */}
      <div className="mt-3 flex items-center gap-1.5 text-xs font-bold whitespace-nowrap">
        {hasTrend && (
          <span className={`${isPositive ? 'text-status-active' : 'text-danger'} flex items-center`}>
            {isPositive ? '↑' : '↓'} {formattedTrend}
          </span>
        )}
        <span className="text-text-secondary/80 font-semibold">{trendLabel}</span>
      </div>
    </div>
  )
}
